import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface KeyboardShortcutsContextType {
  isCommandBarOpen: boolean;
  isHelpModalOpen: boolean;
  openCommandBar: () => void;
  closeCommandBar: () => void;
  openHelpModal: () => void;
  closeHelpModal: () => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | undefined>(undefined);

export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutsProvider');
  }
  return context;
};

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({ children }) => {
  const [isCommandBarOpen, setIsCommandBarOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const navigate = useNavigate();

  const openCommandBar = () => setIsCommandBarOpen(true);
  const closeCommandBar = () => setIsCommandBarOpen(false);
  const openHelpModal = () => setIsHelpModalOpen(true);
  const closeHelpModal = () => setIsHelpModalOpen(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Command palette shortcuts
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        openCommandBar();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === 'P') {
        event.preventDefault();
        openCommandBar();
        return;
      }

      // Don't handle shortcuts if command bar is open
      if (isCommandBarOpen) return;

      // Don't handle shortcuts if user is typing in an input
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || 
         activeElement.tagName === 'TEXTAREA' || 
         (activeElement instanceof HTMLElement && activeElement.contentEditable === 'true'))
      ) {
        return;
      }

      // Navigation shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 'd':
            event.preventDefault();
            navigate('/dashboard');
            break;
          case 'c':
            if (!event.shiftKey) {
              event.preventDefault();
              navigate('/dashboard/conversations');
            }
            break;
          case 'p':
            event.preventDefault();
            navigate('/dashboard/contacts');
            break;
          case 'r':
            if (!event.shiftKey) {
              event.preventDefault();
              navigate('/dashboard/reports');
            }
            break;
          case 't':
            event.preventDefault();
            navigate('/dashboard/teams');
            break;
          case 'l':
            event.preventDefault();
            navigate('/dashboard/labels');
            break;
          case 's':
            if (event.shiftKey) {
              event.preventDefault();
              navigate('/dashboard/settings');
            }
            break;
          case 'n':
            event.preventDefault();
            // Navigate to new conversation or open new conversation modal
            navigate('/dashboard/conversations?new=true');
            break;
        }
      }

      // Single key shortcuts (when not in input)
      if (!event.ctrlKey && !event.metaKey && !event.altKey) {
        switch (event.key) {
          case '/':
            event.preventDefault();
            openCommandBar();
            break;
          case '?':
            event.preventDefault();
            openHelpModal();
            break;
          case 'g':
            // 'g' followed by another key for navigation
            setTimeout(() => {
              const handleSecondKey = (secondEvent: KeyboardEvent) => {
                switch (secondEvent.key) {
                  case 'd':
                    navigate('/dashboard');
                    break;
                  case 'c':
                    navigate('/dashboard/conversations');
                    break;
                  case 'p':
                    navigate('/dashboard/contacts');
                    break;
                  case 'r':
                    navigate('/dashboard/reports');
                    break;
                  case 't':
                    navigate('/dashboard/teams');
                    break;
                  case 'l':
                    navigate('/dashboard/labels');
                    break;
                  case 's':
                    navigate('/dashboard/settings');
                    break;
                }
                document.removeEventListener('keydown', handleSecondKey);
              };
              document.addEventListener('keydown', handleSecondKey);
            }, 0);
            break;
        }
      }

      // Conversation-specific shortcuts
      if (window.location.pathname.includes('/conversations/')) {
        if (event.ctrlKey || event.metaKey) {
          switch (event.key) {
            case 'Enter':
              event.preventDefault();
              // Send message or save reply
              console.log('Send message shortcut');
              break;
            case 'e':
              event.preventDefault();
              // Edit last message
              console.log('Edit message shortcut');
              break;
          }
        }

        // Single key shortcuts in conversation
        if (!event.ctrlKey && !event.metaKey && !event.altKey) {
          switch (event.key) {
            case 'r':
              event.preventDefault();
              // Focus reply box
              const replyBox = document.querySelector('textarea[placeholder*="reply"], input[placeholder*="reply"]') as HTMLElement;
              if (replyBox) {
                replyBox.focus();
              }
              break;
            case 'a':
              event.preventDefault();
              // Assign conversation
              console.log('Assign conversation shortcut');
              break;
            case 'c':
              event.preventDefault();
              // Close/resolve conversation
              console.log('Close conversation shortcut');
              break;
            case 's':
              event.preventDefault();
              // Snooze conversation
              console.log('Snooze conversation shortcut');
              break;
            case 'p':
              event.preventDefault();
              // Set priority
              console.log('Set priority shortcut');
              break;
            case 'l':
              event.preventDefault();
              // Add label
              console.log('Add label shortcut');
              break;
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, isCommandBarOpen]);

  const value = {
    isCommandBarOpen,
    isHelpModalOpen,
    openCommandBar,
    closeCommandBar,
    openHelpModal,
    closeHelpModal
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};